
import { computed, defineComponent, onActivated, onMounted, reactive, ref, toRefs } from 'vue'
import SearchForm from '../../../components/form/search/SearchForm.vue'
import SimpleTable from '../../../components/table/simple/SimpleTable.vue'
import Container from '../../../components/form/container/Container.vue'
import { useI18n } from 'vue-i18n'
import { formatDate, formatThousands } from '@/utils'
import { createModel } from '@/utils/component'
import { checkPermission } from '@/utils/permission'
import {
  AcceptanceBillApprovalListParamModel,
  AcceptanceBillApprovalPageParamModel,
  ApproveAcceptanceBillModel
} from '@/model/accepted-bill/approvalFormModel'
import { useRouter } from 'vue-router'
import { getApprovalPagelist, getApprovalPageByUserlist } from '@/apis/accepted-bill/acceptanceReceipt'
import { ElMessage } from 'element-plus'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'
import resize from '@/layout/resize'
import Pagination from '@/components/pagination/Pagination.vue'
import { DeviceType } from '@/store/modules/app/state'

export default defineComponent({
  name: 'ApproveAcceptancePageList',
  components: {
    Pagination,
    SearchForm,
    SimpleTable,
    Container
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const tableRef = ref()
    const { device } = resize()
    const labelWidth = computed(() => {
      if (device.value === DeviceType.Mobile) {
        return '100px'
      } else {
        return '150px'
      }
    })
    const receiveTypeOptions = [
      { label: t('Acceptance.signForReceipt'), value: 1 },
      { label: t('Acceptance.applicationRejected'), value: 2 }
    ]
    const statusOptions = [
      { label: t('approvalForm.pendingLevel1Approval'), value: 1 },
      { label: t('approvalForm.pendingLevel2Approval'), value: 2 },
      { label: t('approvalForm.pendingLevel3Approval'), value: 3 }
    ]
    const searchStruct = [
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      {
        prop: 'receiveType',
        label: t('Acceptance.approvalType'),
        placeholder: t('Acceptance.all'),
        component: 'el-select',
        type: 'text',
        model: null,
        options: receiveTypeOptions
      },
      { prop: 'approver', label: t('Acceptance.currentApprover'), component: 'el-input', type: 'text', model: '' }
    ]
    const approveAcceptanceTableStruct = [
      { prop: 'id', label: t('Acceptance.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'submitterName', label: t('Acceptance.Submitter') },
      { prop: 'submitDate', label: t('Acceptance.submitTime'), formatter: (row: ApproveAcceptanceBillModel) => formatDate(row.submitDate, 'yyyy-MM-dd') },
      { prop: 'approverName', label: t('Acceptance.currentApprover') },
      { prop: 'totalDraftAmt', label: t('Acceptance.draftAmt'), formatter: (row: any) => formatThousands(row.totalDraftAmt), align: 'right' },
      {
        prop: 'receiveType',
        label: t('Acceptance.approvalType'),
        formatter: (row: ApproveAcceptanceBillModel) => {
          return receiveTypeOptions.find(item => item.value === row.receiveType)?.label
        }
      },
      {
        prop: 'status',
        label: t('Acceptance.status'),
        formatter: (row: ApproveAcceptanceBillModel) => {
          return statusOptions.find(item => item.value === row.status)?.label
        }
      }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as AcceptanceBillApprovalListParamModel,
      tableData: Array<ApproveAcceptanceBillModel>(),
      columnStruct: approveAcceptanceTableStruct,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true

        const params: AcceptanceBillApprovalPageParamModel = {
          companyCode: dataMap.searchFormModel.companyCode,
          status: null,
          result: null,
          receiveType: dataMap.searchFormModel.receiveType,
          approver: dataMap.searchFormModel.approver,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        let data = null
        if (checkPermission(['ACC_APPROVAL_R'])) {
          data = await getApprovalPagelist(params)
        } else if (checkPermission(['ACC_APPROVAL_SR'])) {
          data = await getApprovalPageByUserlist(params)
        } else {
          data = await getApprovalPagelist(params)
        }
        // const data = await getApprovalPagelist(params)
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list
        } else {
          dataMap.tableData = []
          if (data?.code !== 200 && data?.msg) {
            ElMessage({
              message: data.msg,
              type: 'error',
              duration: 2000
            })
          }
        }
        dataMap.total = data?.data?.totalCount ?? 0

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      }
    })
    const viewDetail = (row: ApproveAcceptanceBillModel) => {
      router.push({ path: `acceptanceApprovalDetail/${row.id}` })
    }
    const getCompanyList = async() => {
      dataMap.searchFormStruct[0].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.searchFormStruct[0].options = optionList
      dataMap.searchFormStruct[0].loading = false
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
      getCompanyList()
    })
    onActivated(async() => {
      if (history.state?.refresh === true) {
        await dataMap.getPageList()
        // 用完删除
        const state = { ...history.state }
        delete state.refresh
        history.replaceState(state, '')
      }
    })
    return {
      t,
      tableRef,
      labelWidth,
      viewDetail,
      ...toRefs(dataMap)
    }
  }
})
